import type { ParsedDateRange } from '@/lib/parseDateRange';
import type { AffiliateData } from '@/services/payment/payment.types';

/** Whole month as a ms range, from the first day 00:00 to the last day 23:59:59.999. */
export const getMonthDateRange = (monthNumber: number, year: number): ParsedDateRange => ({
  start: new Date(year, monthNumber - 1, 1).getTime(),
  end: new Date(year, monthNumber, 0, 23, 59, 59, 999).getTime(),
});

/** Whole-month range as "start,end" in ms: the format the table date filter keeps in the URL. */
export const getMonthDateRangeValue = (monthNumber: number, year: number): string => {
  const { start, end } = getMonthDateRange(monthNumber, year);

  return `${start},${end}`;
};

/**
 * List rows carry only the month number, so take the year whose month overlaps the selected range.
 * Exact for any range shorter than 12 months, including ones that cross a year boundary.
 */
export const resolveMonthYear = (monthNumber: number, range: ParsedDateRange): number => {
  const startYear = new Date(range.start).getFullYear();
  const endYear = new Date(range.end).getFullYear();

  for (let year = startYear; year <= endYear; year++) {
    const month = getMonthDateRange(monthNumber, year);
    if (month.start <= range.end && month.end >= range.start) return year;
  }

  return startYear;
};

/** The row's year from the API, or (until the API sends it) the year resolved from the selected range. */
export const getRowYear = (
  { year, monthNumber }: Pick<AffiliateData, 'year' | 'monthNumber'>,
  range: ParsedDateRange,
) => year ?? resolveMonthYear(monthNumber, range);
